/**
 * `#uc2Panel-trends`' own content (design `2026-10-02-uc2-tabs-analytics-design.md`
 * §Tabs, extended by the client round of 3 Oct 2026): five blocks that say something
 * **before** anything is clicked —
 *
 * * `#uc2TrendCroplandUnits` — farmland share by year, **one line per unit**: per
 *   watershed at watershed level, per country at country level;
 * * `#uc2TrendRainUnits` — rainfall by year, the same way round;
 *
 * * `#uc2TrendCropland` — farmland share by year, the watersheds with positive change
 *   against the rest, two lines on one plot;
 * * `#uc2TrendRain` — the same two groups' rainfall with the period average across it, so
 *   a reader can see for themselves whether the two groups simply had different weather;
 * * `#uc2TrendGreening` — every watershed's vegetation trend, ranked, each bar coloured by
 *   its class.
 *
 * The three class blocks read `uc2/summary`'s precomputed blocks for the current **scope** (the
 * programme at watershed level, one country at country level) — no request of their own.
 * With a watershed selected, its own series from the detail the click already fetched is
 * drawn **bold** over the two class lines, and its bar is outlined in the ranking.
 *
 * The two per-unit blocks read one series per unit. At country level that is
 * `by_class_series[<country>].all`, already on the summary. At watershed level it is each
 * unit's own `series`, which lives on `uc2/units/{id}` — so this component, and only
 * while the Trends tab is mounted, fans `useQueries` out over the eleven ids. They are the
 * very cache entries a click already fills, keyed identically, so the detail of a
 * watershed the reader has clicked costs nothing twice and the tab is paid for once per
 * session rather than per visit.
 *
 * A block the run does not carry yet prints one short note rather than an empty frame.
 */
import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  fetchIntelUnitDetail,
  queryKeys,
  type IntelSeriesPoint,
  type IntelUc2ClassSeriesScope,
  type IntelUc2GreeningUnit,
  type IntelUnitDetail,
  type IntelUnitRow,
} from '../../lib/api';
import { DimensionBars, type DimRow } from './charts/DimensionBars';
import { LineSeries, type LineSeriesLine } from './charts/LineSeries';
import { classRainCaption, classTrendCaption, greeningCaption } from './uc2Captions';
import {
  SELECTED_OUTLINE_COLOR,
  UC2_CATEGORY_COLORS,
  UC2_REST_COLOR,
  UC2_UNIT_LINE_COLORS,
  NEUTRAL_COLOR,
  byValueDesc,
  unitInScope,
} from './uc2Constants';

export interface Uc2TrendsProps {
  /** `summary.by_class_series[scope]`, or `undefined` while the run carries none. */
  byClass?: IntelUc2ClassSeriesScope | undefined;
  /** `summary.greening_by_unit`, the whole programme — filtered to the scope here. */
  greeningByUnit?: IntelUc2GreeningUnit[] | undefined;
  scope: string;
  /** The run's own stability band, in percent, for the two captions. */
  band?: number;
  /** The clicked watershed's detail, or `null` — a country row draws no series here. */
  detail: IntelUnitDetail | null;
  /** `#intelLevel` — `'watershed'` or `'admin0'`, which decides what a per-unit line is. */
  level: string;
  /** The rows of the current level: one line per row in the two per-unit charts. */
  units: IntelUnitRow[];
  /** `summary.by_class_series` whole, for the country lines (`[<country>].all`). */
  byClassSeries?: Record<string, IntelUc2ClassSeriesScope> | undefined;
  /** The focused unit — its line is bold and the rest are thinned. */
  selectedId: string | null;
}

export function Uc2Trends({
  byClass,
  greeningByUnit,
  scope,
  band,
  detail,
  level,
  units,
  byClassSeries,
  selectedId,
}: Uc2TrendsProps) {
  const { t } = useTranslation();
  const watershed = detail && detail.unit.level !== 'admin0' ? detail : null;
  const isCountry = level === 'admin0';

  /**
   * One `uc2/units/{id}` per watershed, for the two per-unit charts. Mounted only with
   * the Trends tab, disabled at country level (where the summary already carries one
   * series per country), and keyed exactly as `useIntelUnitDetail` keys a click — so
   * this is one request per watershed per session, shared with every other reader of the
   * same entry, and nothing is re-fetched when the reader comes back to the tab.
   */
  const unitDetails = useQueries({
    queries: units.map((row) => ({
      queryKey: queryKeys.intelUnit('uc2', row.unit_id),
      queryFn: () => fetchIntelUnitDetail('uc2', row.unit_id),
      enabled: !isCountry,
      // Immutable for the life of the run, and `gcTime` beside `staleTime` is what makes
      // that true across a tab switch: without it the eleven entries are collected a few
      // minutes after Trends unmounts, and coming back re-fetches all of them.
      staleTime: Infinity,
      gcTime: Infinity,
    })),
  });

  /**
   * The eleven per-unit requests are still in flight. The two per-unit blocks must not
   * print `blockPending` while they are: "this run does not carry that block" is a claim
   * about the data, and saying it for the second it takes the fan-out to answer tells the
   * reader their run is missing something it is about to draw.
   */
  const unitsLoading = !isCountry && unitDetails.some((q) => q.isPending);

  const note = (testId: string) => (
    <p className="note" data-testid={testId}>
      {t('intel.uc2.blockPending')}
    </p>
  );

  /** The same frame while the fan-out answers — a wait, not a verdict. */
  const loading = (testId: string) => (
    <p className="note" data-testid={testId}>
      {t('intel.uc2.loading')}
    </p>
  );

  /** One class's series as the chart's own points — `pct` for cropland, `mm` for rainfall. */
  const points = (
    rows: { year: number; pct?: number | null; mm?: number | null }[] | undefined,
    field: 'pct' | 'mm',
  ) => (rows ?? []).map((p) => ({ year: p.year, value: (p[field] ?? null) as number | null }));

  const lineFor = (
    id: string,
    label: string,
    color: string,
    rows: { year: number; pct?: number | null; mm?: number | null }[] | undefined,
    field: 'pct' | 'mm',
    highlight = false,
  ): LineSeriesLine | null => {
    const pts = points(rows, field);
    if (!pts.some((p) => p.value != null)) return null;
    return { id, label, color, points: pts, ...(highlight ? { highlight } : {}) };
  };

  const croplandLines = [
    lineFor('positive', t('intel.uc2.category.positive'), UC2_CATEGORY_COLORS.positive!, byClass?.positive?.cropland, 'pct'),
    lineFor('no_pattern', t('intel.uc2.category.no_pattern'), UC2_REST_COLOR, byClass?.no_pattern?.cropland, 'pct'),
    watershed
      ? lineFor(
          watershed.unit.id,
          watershed.unit.name,
          SELECTED_OUTLINE_COLOR,
          watershed.series?.cropland,
          'pct',
          true,
        )
      : null,
  ].filter((l): l is LineSeriesLine => l != null);

  const rainLines = [
    lineFor('positive', t('intel.uc2.category.positive'), UC2_CATEGORY_COLORS.positive!, byClass?.positive?.rain, 'mm'),
    lineFor('no_pattern', t('intel.uc2.category.no_pattern'), UC2_REST_COLOR, byClass?.no_pattern?.rain, 'mm'),
    watershed
      ? lineFor(
          watershed.unit.id,
          watershed.unit.name,
          SELECTED_OUTLINE_COLOR,
          watershed.series?.rain,
          'mm',
          true,
        )
      : null,
  ].filter((l): l is LineSeriesLine => l != null);

  // How many watersheds the positive line is the mean of — the captions say it, because
  // "positive areas" over two watersheds and over twenty are not the same claim.
  const nPositive = byClass?.n?.positive ?? null;
  const croplandCaption = classTrendCaption(
    points(byClass?.positive?.cropland, 'pct'),
    band,
    nPositive,
  );
  const rainTextCaption = classRainCaption(
    points(byClass?.positive?.rain, 'mm'),
    points(byClass?.no_pattern?.rain, 'mm'),
    band,
    nPositive,
  );
  // The dashed line across the rainfall plot: the scope's own average over the period —
  // "period average", not "programme average", because at country level it is that
  // country's. This is the design's "two lines + average".
  const rainMean = (() => {
    const all = points(byClass?.all?.rain, 'mm')
      .map((p) => p.value)
      .filter((v): v is number => typeof v === 'number');
    if (!all.length) return null;
    return all.reduce((a, b) => a + b, 0) / all.length;
  })();

  /**
   * One line per unit, for the two per-unit charts. At watershed level the points are the
   * unit's own `series`; at country level they are that country's `all` series on the
   * summary. A unit with nothing measured contributes no line at all rather than a flat
   * line at the bottom of the plot.
   */
  const unitLines = (field: 'cropland' | 'rain', value: 'pct' | 'mm'): LineSeriesLine[] =>
    units
      .map((row, i): LineSeriesLine | null => {
        const rows: IntelSeriesPoint[] | undefined = isCountry
          ? byClassSeries?.[row.unit_id]?.all?.[field]
          : unitDetails[i]?.data?.series?.[field];
        return lineFor(
          row.unit_id,
          row.name,
          UC2_UNIT_LINE_COLORS[i % UC2_UNIT_LINE_COLORS.length]!,
          rows,
          value,
          row.unit_id === selectedId,
        );
      })
      .filter((line): line is LineSeriesLine => line != null);

  const croplandUnitLines = unitLines('cropland', 'pct');
  const rainUnitLines = unitLines('rain', 'mm');
  const unitsTitle = (key: 'croplandUnits' | 'rainUnits') =>
    t(`intel.uc2.chart.${key}${isCountry ? 'Country' : ''}`);

  const greeningRows = (greeningByUnit ?? []).filter((r) => unitInScope(r.unit_id, scope));
  const ranked = [...greeningRows].sort((a, b) => byValueDesc(a.ndvi_trend, b.ndvi_trend));
  const greeningBars: DimRow[] = ranked.map((row) => ({
    id: row.unit_id,
    label: row.name,
    value: typeof row.ndvi_trend === 'number' ? row.ndvi_trend : null,
    unit: t('intel.uc2.chart.perYear'),
    decimals: 4,
    color: UC2_CATEGORY_COLORS[row.category] ?? NEUTRAL_COLOR,
  }));
  const greeningText = greeningCaption(ranked);

  return (
    <>
      {/* The two per-unit plots first (client round 3 Oct 2026): a reader opening Trends
          wants to see the places, and the class means underneath are the comparison those
          places are read against. */}
      <div id="uc2TrendCroplandUnits" className="uc2-chart intel-card">
        <h6 className="intel-card-title">{unitsTitle('croplandUnits')}</h6>
        {croplandUnitLines.length ? (
          <LineSeries
            series={croplandUnitLines}
            unit="%"
            ariaLabel={unitsTitle('croplandUnits')}
            testId="uc2-trend-cropland-units"
          />
        ) : unitsLoading ? (
          loading('uc2-trend-cropland-units-loading')
        ) : (
          note('uc2-trend-cropland-units-empty')
        )}
      </div>

      <div id="uc2TrendRainUnits" className="uc2-chart intel-card">
        <h6 className="intel-card-title">{unitsTitle('rainUnits')}</h6>
        {rainUnitLines.length ? (
          <LineSeries
            series={rainUnitLines}
            unit="mm"
            decimals={0}
            // Rainfall is read for the gap between the units, not for its distance from
            // no rain at all — the class plot's own reason.
            zeroBaseline={false}
            ariaLabel={unitsTitle('rainUnits')}
            testId="uc2-trend-rain-units"
          />
        ) : unitsLoading ? (
          loading('uc2-trend-rain-units-loading')
        ) : (
          note('uc2-trend-rain-units-empty')
        )}
      </div>

      <div id="uc2TrendCropland" className="uc2-chart intel-card">
        <h6 className="intel-card-title">{t('intel.uc2.chart.trendCropland')}</h6>
        {croplandLines.length ? (
          <>
            <LineSeries
              series={croplandLines}
              unit="%"
              ariaLabel={t('intel.uc2.chart.trendCropland')}
              testId="uc2-trend-cropland"
            />
            {croplandCaption ? (
              <p className="sub" data-testid="uc2-trend-cropland-caption">
                {t(croplandCaption.key, croplandCaption.options ?? {})}
              </p>
            ) : null}
          </>
        ) : (
          note('uc2-trend-cropland-empty')
        )}
      </div>

      <div id="uc2TrendRain" className="uc2-chart intel-card">
        <h6 className="intel-card-title">{t('intel.uc2.chart.trendRain')}</h6>
        {rainLines.length ? (
          <>
            <LineSeries
              series={rainLines}
              unit="mm"
              decimals={0}
              mean={rainMean}
              meanLabel={t('intel.uc2.chart.periodMean')}
              // Rainfall is read for the gap between the two groups, not for its distance
              // from no rain at all: a 0–900 mm plot would press both lines into one band.
              zeroBaseline={false}
              ariaLabel={t('intel.uc2.chart.trendRain')}
              testId="uc2-trend-rain"
            />
            {rainTextCaption ? (
              <p className="sub" data-testid="uc2-trend-rain-caption">
                {t(rainTextCaption.key, rainTextCaption.options ?? {})}
              </p>
            ) : null}
          </>
        ) : (
          note('uc2-trend-rain-empty')
        )}
      </div>

      <div id="uc2TrendGreening" className="uc2-chart intel-card">
        <h6 className="intel-card-title">{t('intel.uc2.chart.trendGreening')}</h6>
        {greeningBars.some((r) => r.value != null) ? (
          <>
            {/* No reference on these rows, so no reference wording either. */}
            <DimensionBars
              rows={greeningBars}
              ariaLabel={t('intel.uc2.chart.trendGreening')}
              selectedId={watershed?.unit.id ?? null}
            />
            {greeningText ? (
              <p className="sub" data-testid="uc2-trend-greening-caption">
                {t(greeningText.key, greeningText.options ?? {})}
              </p>
            ) : null}
          </>
        ) : (
          note('uc2-trend-greening-empty')
        )}
      </div>
    </>
  );
}

export default Uc2Trends;
